// Project Manager Persona Definitions
// All managers are influencer marketing experts, but specialize in different project types

export interface ManagerPersona {
  id: string;
  name: string;
  avatar: string; // Emoji representation
  role: string;
  description: string;
  responsibilities: string[];
  bestFor: string[];
  personality: string[];
  color: string; // Theme color for this manager
  campaignType: 'sponsored_content' | 'affiliate' | 'ambassadorship' | 'product_seeding' | 'custom';
}

export const MANAGER_PERSONAS: ManagerPersona[] = [
  {
    id: 'campaign-carl',
    name: 'Campaign Carl',
    avatar: '📢',
    role: 'Sponsored Content Specialist',
    description: 'Expert in influencer marketing, specializing in sponsored content campaigns. I handle outreach, negotiate rates, track deliverables, and ensure content gets approved and posted on time.',
    responsibilities: [
      'Recruit creators within budget',
      'Negotiate rates and deliverables',
      'Manage content approval workflow',
      'Track posting deadlines',
      'Monitor campaign results'
    ],
    bestFor: [
      'One-time paid campaigns',
      'Multiple creators with tight deadlines',
      'Need approval workflow',
      'Fixed budget per creator'
    ],
    personality: [
      'Organized and deadline-driven',
      'Budget-conscious',
      'Clear communicator',
      'Proactive problem solver'
    ],
    color: '#3b82f6', // Blue
    campaignType: 'sponsored_content'
  },
  {
    id: 'affiliate-alice',
    name: 'Affiliate Alice',
    avatar: '💰',
    role: 'Affiliate Marketing Specialist',
    description: 'Expert in influencer marketing, specializing in affiliate programs. I set up tracking links, negotiate commissions, monitor performance, and optimize for sales.',
    responsibilities: [
      'Set up affiliate tracking',
      'Negotiate commission structures',
      'Monitor conversion rates',
      'Identify top performers',
      'Optimize for revenue'
    ],
    bestFor: [
      'Commission-based campaigns',
      'Pay for performance only',
      'Need tracking setup',
      'Long-term revenue focus'
    ],
    personality: [
      'Data-obsessed',
      'Optimization-focused',
      'Patient with growth',
      'Analytical mindset'
    ],
    color: '#10b981', // Green
    campaignType: 'affiliate'
  },
  {
    id: 'ambassador-adam',
    name: 'Ambassador Adam',
    avatar: '👑',
    role: 'Brand Ambassador Specialist',
    description: 'Expert in influencer marketing, specializing in long-term brand ambassadorships. I handle contracts, monthly check-ins, and ensure ambassadors hit their quotas.',
    responsibilities: [
      'Build long-term relationships',
      'Manage monthly check-ins',
      'Track quota completion',
      'Handle contract renewals',
      'Nurture brand advocates'
    ],
    bestFor: [
      'Multi-month partnerships',
      'Want ongoing relationships',
      'Monthly content quotas',
      'Exclusivity agreements'
    ],
    personality: [
      'Relationship-focused',
      'Strategic thinker',
      'Long-term oriented',
      'Trust builder'
    ],
    color: '#8b5cf6', // Purple
    campaignType: 'ambassadorship'
  },
  {
    id: 'product-seeding-sam',
    name: 'Product Seeding Sam',
    avatar: '📦',
    role: 'Product Seeding Specialist',
    description: 'Expert in influencer marketing, specializing in product gifting campaigns. I handle shipping logistics, track deliveries, follow up on content, and build organic relationships.',
    responsibilities: [
      'Manage shipping logistics',
      'Track delivery confirmations',
      'Follow up on content',
      'Build organic relationships',
      'No payment hassles'
    ],
    bestFor: [
      'Product gifting campaigns',
      'No budget / products only',
      'Want organic content',
      'Building brand awareness'
    ],
    personality: [
      'Logistics expert',
      'Relationship builder',
      'Patient and understanding',
      'Detail-oriented'
    ],
    color: '#f59e0b', // Orange
    campaignType: 'product_seeding'
  },
  {
    id: 'general-manager',
    name: 'General Manager',
    avatar: '💼',
    role: 'Custom Campaign Specialist',
    description: 'Expert in influencer marketing, specializing in custom campaigns. Tell me what you need, and I\'ll adapt to your specific workflow and requirements.',
    responsibilities: [
      'Adapt to your needs',
      'Custom workflow setup',
      'Flexible approach',
      'Learn as we go',
      'Handle unique scenarios'
    ],
    bestFor: [
      'Custom campaign types',
      'Unique workflows',
      'Hybrid approaches',
      'Experimental campaigns'
    ],
    personality: [
      'Flexible and adaptive',
      'Quick learner',
      'Open to experimentation',
      'Creative problem solver'
    ],
    color: '#6b7280', // Gray
    campaignType: 'custom'
  }
];

// Helper to get manager by ID
export const getManagerById = (id: string): ManagerPersona | undefined => {
  return MANAGER_PERSONAS.find(m => m.id === id);
};

// Helper to get managers by campaign type
export const getManagersByCampaignType = (type: string): ManagerPersona[] => {
  return MANAGER_PERSONAS.filter(m => m.campaignType === type || m.campaignType === 'custom');
};

